/**
 * WS-ELIG B-E3 (constitution closure 2026-10-19) — a roster entry is not a worker until the person accepts.
 *
 * CORE-W:43 "An employee joins by invitation and their own acceptance." CORE-W:76 "No automated process may add a
 * person to a shop, and the only path that creates the operational relationship is one the person initiated by
 * accepting." CORE-W:45 allows a pre-seeded roster entry; acceptance adopts it (B-E4).
 *
 * So the New Technician form, in a shop that ENFORCES real employee eligibility, asks for the person's email and
 * sends them an invitation that names the new roster entry; their acceptance makes them schedulable and adopts the
 * entry. In an observe shop the email is optional (the legacy roster still schedules there) — if given, the same
 * invitation is sent.
 */
import { supabase } from '@/integrations/supabase/client';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Why the New Technician form cannot be saved yet, or null. `enforced` null = unknown → not required. */
export function newTechnicianInvitationProblem(enforced: boolean | null, email: string): string | null {
  const e = email.trim();
  if (e.length > 0 && !EMAIL.test(e)) return 'Enter a valid email address for the invitation.';
  if (enforced === true && e.length === 0) {
    return 'This shop only schedules people who have joined. Enter their email so they can be invited — they are not on the board until they accept.';
  }
  return null;
}

/** The notice the form shows for a NEW entry. */
export function newTechnicianInvitationNotice(enforced: boolean | null): string {
  return enforced === true
    ? 'They will be invited to join. They are not on the board and cannot be scheduled until they accept the invitation.'
    : 'Optional. If you enter their email they are invited to join with their own OptiBay account, which takes over this roster entry.';
}

export async function fetchShopWorkEligibilityEnforced(shopId: string): Promise<boolean | null> {
  if (!shopId) return null;
  try {
    const { data, error } = await supabase.rpc('shop_work_eligibility_enforced' as never, { p_shop_id: shopId } as never);
    if (error) return null;
    return (data as unknown) === true;
  } catch {
    return null; // unknown — the server's guards still decide
  }
}

/** Invite the person to the roster entry just created (names it, so acceptance adopts it). */
export async function inviteToRosterEntry(input: {
  shopId: string;
  shopName?: string | null;
  email: string;
  technicianId: string;
}): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.functions.invoke('send-team-invitation', {
    body: {
      shopId: input.shopId,
      email: input.email.trim().toLowerCase(),
      authorityRole: 'STAFF',
      workRoles: ['TECHNICIAN'],
      shopName: input.shopName ?? undefined,
      technicianId: input.technicianId,
    },
  });
  return error ? { ok: false, error: (error as { message?: string }).message ?? 'Invitation failed' } : { ok: true };
}
